import React, { useCallback, useEffect, useState } from "react"
import { NavLink, useNavigate, useParams } from "react-router-dom"
import { MdDeleteOutline, MdFilePresent, MdOutlineErrorOutline, MdVisibility } from "react-icons/md"
import { toast } from "react-toastify"
import api from "../../services/api"
import ModalConfirm from "../../components/Modal/ModalConfirm"
import BulkActionHistory from "../ManagementLog/BulkActionHistory"
import { openLinkExternal } from "../../utils/utils"

const ImportLog = () => {
    const [imports, setImports] = useState([])
    const [idDiscardImport, setIdDiscardImport] = useState("")
    const [isLoading, setIsLoading] = useState(false)
    const { account_id } = useParams()
    const navigate = useNavigate()

    const getAllImports = useCallback(async ({ silent = false } = {}) => {
        try {
            if (!silent) setIsLoading(true)
            const response = await api.get("/imports")
            setImports(response.data?.data || [])
        } catch (err) {
            if (!silent) toast.error(err?.response?.data?.message || "Não foi possível carregar as importações.")
        } finally {
            if (!silent) setIsLoading(false)
        }
    }, [])

    useEffect(() => {
        getAllImports()
    }, [getAllImports])

    const handleViewImport = async item => {
        try {
            setIsLoading(true)
            const response = await api.get(`/imports/${item._id}/view`)
            openLinkExternal(response.data.data)
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível abrir os dados enviados.")
        } finally {
            setIsLoading(false)
        }
    }

    const handleDiscardImport = async () => {
        try {
            setIsLoading(true)
            const response = await api.patch(`/imports/${idDiscardImport}/discard`)
            setIdDiscardImport("")
            toast.success(response?.data?.message)
            await getAllImports({ silent: true })
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível descartar a importação.")
        } finally {
            setIsLoading(false)
        }
    }

    const data = imports.map(item => {
        const discard = item.discard_execution
        const status = discard?.status
            || (item.imported ? (item.error ? "completed_with_errors" : "completed") : "processing")
        const count = discard?.count ?? item.count ?? 0
        const countUpdated = discard?.count_updated ?? (item.imported ? count : 0)
        return {
            ...item,
            action_label: discard ? "Descarte de importação" : "Importação de leads",
            action_summary: discard
                ? `${item.name || "Importação"} · envio dos leads para a lixeira`
                : `${item.name || "Importação"} · ${item.count || 0} registros enviados`,
            status,
            count,
            count_updated: countUpdated,
            count_failed: discard?.count_failed || 0,
            progress: count ? Math.round((countUpdated / count) * 100) : (status === "completed" ? 100 : 0),
            error: discard?.error || (item.error ? "Uma ou mais linhas apresentaram falha durante a importação." : null),
            updated_at: discard?.updated_at || item.updated_at,
        }
    })

    return (
        <>
            <BulkActionHistory
                data={data}
                title="Histórico de Importações"
                description="Acompanhe o processamento, as falhas e o eventual descarte de cada importação."
                isLoading={isLoading}
                onRefresh={getAllImports}
                progressLabel="Registros processados"
                getActionIcon={() => MdFilePresent}
                getMetadata={item => []}
                getDetails={item => [
                    { label: "Identificação", value: item.name || "-" },
                    { label: "Tipo", value: item.type || "-" },
                    { label: "Registros enviados", value: item.count || 0 },
                    { label: "Última atualização", value: item.updated_at || "-" },
                    { label: "Identificador", value: item._id, executionId: true },
                    ...(item.discard_execution?.started_at ? [{ label: "Início do descarte", value: item.discard_execution.started_at }] : []),
                    ...(item.discard_execution?.finished_at ? [{ label: "Conclusão do descarte", value: item.discard_execution.finished_at }] : []),
                ]}
                getActions={item => [
                    { label: "Dados enviados", icon: MdVisibility, onClick: handleViewImport },
                    ...(item.error && !item.discarted ? [{ label: "Ver falhas", icon: MdOutlineErrorOutline, onClick: current => navigate(`/${account_id}/import/${current._id}/error`) }] : []),
                    ...(item.discard_is_valid ? [{ label: "Descartar", icon: MdDeleteOutline, tone: "danger", onClick: current => setIdDiscardImport(current._id) }] : []),
                ]}
                headerAction={<NavLink to={`/${account_id}/import`}>Nova importação</NavLink>}
            />

            {idDiscardImport &&
                <ModalConfirm
                    icon={<MdDeleteOutline />}
                    title="Descartar leads importados"
                    description="Deseja realmente descartar os leads importados?"
                    text="A exclusão será executada em uma fila controlada e poderá ser acompanhada neste histórico."
                    onSubmit={handleDiscardImport}
                    closeModal={() => setIdDiscardImport("")}
                />
            }
        </>
    )
}

export default ImportLog
